import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import NotificationBanner from "./components/NotificationBanner";
import VisitorTracker from "./utils/visitorTracking";
import BetaGate from "./components/BetaGate";

// Layout
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import RegisterLabRep from "./pages/Auth/RegisterLabRep";
import Pricing from "./pages/Pricing";
import Professionals from "./pages/Professionals";
import PatientDashboard from "./pages/Dashboard/PatientDashboard";
import ProfessionalDashboard from "./pages/Dashboard/ProfessionalDashboard";
import MedicalRepDashboard from "./pages/Dashboard/MedicalRepDashboard";
import CabinetManagement from "./pages/Dashboard/CabinetManagement";
import SuperAdminDashboard from "./pages/Admin/SuperAdminDashboard";
import ProvidersManager from "./pages/Admin/ProvidersManager";
import Teleconsultation from "./pages/Teleconsultation/Teleconsultation";
import PaymentPage from "./pages/Payment/PaymentPage";

// Online Consultations (EU Cross-border)
import { ConsultationSearch, ProfessionalConsultationSettings, MyConsultations, AdminConsultationStats } from "./pages/Consultations/OnlineConsultations";
import { PatientTutorial, ProfessionalTutorial } from "./pages/Consultations/TutorialPages";

// Static Pages
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Blog from "./pages/Blog";
import Marketing from "./pages/Marketing";

// Settings
import PrivacySettings from "./pages/Settings/PrivacySettings";
import InvoicesPage from "./pages/Settings/InvoicesPage";
import TwoFactorSettings from "./pages/Settings/TwoFactorSettings";
import NotificationSettings from "./pages/Settings/NotificationSettings";
import MedicalHistory from "./pages/Settings/MedicalHistory";
import LoyaltyProgram from "./pages/Settings/LoyaltyProgram";
import ReferralProgram from "./pages/Settings/ReferralProgram";
import CalendarSettings from "./pages/Settings/CalendarSettings";

// Legal Pages
import TermsOfService from "./pages/Legal/TermsOfService";
import LegalNotice from "./pages/Legal/LegalNotice";

// Email Verification
import VerifyEmail from "./pages/VerifyEmail";
import EmailNotVerified from "./pages/EmailNotVerified";

// Professional Pages
import MultiLocationManager from "./pages/Professional/MultiLocationManager";
import ProfessionalContract from "./pages/Professional/ProfessionalContract";
import CalendarSync from "./pages/Professional/CalendarSync";
import AdvancedSearch from "./pages/AdvancedSearch";
import B2BAdvertising from "./pages/Professional/B2BAdvertising";

// Components
import CookieConsent from "./components/CookieConsent";
import PWAStatusBar from "./components/PWAStatusBar";
import CookieBanner from "./components/CookieBanner";
import SessionTimeout from "./components/SessionTimeout";

// 404 Page
import NotFound from "./pages/NotFound";

// Protected Route Component with Email Verification Check
const ProtectedRoute = ({ children, allowedRoles, requireEmailVerification = false }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check email verification (disabled by default - enable when domain is configured)
  // Set requireEmailVerification = true to enable
  if (requireEmailVerification && user?.role !== 'super_admin' && !user?.email_verified) {
    return <Navigate to="/email-not-verified" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'country_admin') return <Navigate to="/country-admin" replace />;
    if (user?.role === 'professional') return <Navigate to="/professional" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Layout wrapper
const Layout = ({ children, showFooter = true }) => {
  return (
    <>
      <Header />
      <main className="min-h-[calc(100vh-64px)]">
        {children}
      </main>
      {showFooter && <Footer />}
    </>
  );
};

// BetaGate protects the app during testing phase

function AppRoutes() {
  return (
    <BetaGate>
    <VisitorTracker>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout><Landing /></Layout>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/laboratoire" element={<RegisterLabRep />} />
        <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
        <Route path="/professionals" element={<Layout><Professionals /></Layout>} />
        
        {/* Static Pages */}
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/faq" element={<Layout><FAQ /></Layout>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/terms" element={<Layout><Terms /></Layout>} />
        
        {/* Legal Pages */}
        <Route path="/cgu" element={<Layout><TermsOfService /></Layout>} />
        <Route path="/mentions-legales" element={<Layout><LegalNotice /></Layout>} />
        
        {/* Email Verification */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route 
          path="/email-not-verified" 
          element={
            <ProtectedRoute requireEmailVerification={false}>
              <EmailNotVerified />
            </ProtectedRoute>
          } 
        />
        
        {/* Blog */}
        <Route path="/blog" element={<Layout><Blog /></Layout>} />
        
        {/* Marketing Page */}
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/avantages" element={<Marketing />} />
        
        {/* Advanced Search */}
        <Route path="/search" element={<Layout><AdvancedSearch /></Layout>} />
        
        {/* Patient Dashboard */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <Layout showFooter={false}>
                <PatientDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Professional Dashboard */}
        <Route 
          path="/professional" 
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Layout showFooter={false}>
                <ProfessionalDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Cabinet Management */}
        <Route 
          path="/cabinet" 
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Layout showFooter={false}>
                <CabinetManagement />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Super Admin Dashboard */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout showFooter={false}>
                <SuperAdminDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Providers Manager */}
        <Route 
          path="/admin/providers" 
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout showFooter={false}>
                <ProvidersManager />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Country Admin Dashboard - Using SuperAdmin for now */}
        <Route 
          path="/country-admin" 
          element={
            <ProtectedRoute allowedRoles={['country_admin', 'super_admin']}>
              <Layout showFooter={false}>
                <SuperAdminDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Settings - Redirect to dashboard for now */}
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <PatientDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Medical Rep Dashboard */}
        <Route 
          path="/medical-rep/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['medical_rep']}>
              <Layout showFooter={false}>
                <MedicalRepDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Privacy Settings */}
        <Route 
          path="/settings/privacy" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <PrivacySettings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Multi-Location Manager (Professional) */}
        <Route 
          path="/professional/locations" 
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Layout showFooter={false}>
                <MultiLocationManager />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Professional Contract */}
        <Route 
          path="/professional/contract" 
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Layout showFooter={false}>
                <ProfessionalContract />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Calendar Sync */}
        <Route 
          path="/professional/calendar-sync" 
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Layout showFooter={false}>
                <CalendarSync />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* B2B Advertising for Professionals/Clinics */}
        <Route 
          path="/professional/advertising" 
          element={
            <ProtectedRoute allowedRoles={['professional', 'super_admin']}>
              <Layout showFooter={false}>
                <B2BAdvertising />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Invoices */}
        <Route 
          path="/settings/invoices" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <InvoicesPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Two-Factor Authentication */}
        <Route 
          path="/settings/2fa" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <TwoFactorSettings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Notification Settings */}
        <Route 
          path="/settings/notifications" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <NotificationSettings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Medical History */}
        <Route 
          path="/settings/medical-history" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <MedicalHistory />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Loyalty Program */}
        <Route 
          path="/settings/loyalty" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <LoyaltyProgram />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Referral Program */}
        <Route 
          path="/settings/referral" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <ReferralProgram />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Calendar Sync Settings */}
        <Route 
          path="/settings/calendar" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <CalendarSettings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Teleconsultation */}
        <Route 
          path="/teleconsultation/:roomId" 
          element={
            <ProtectedRoute>
              <Teleconsultation />
            </ProtectedRoute>
          } 
        />
        
        {/* Online Consultations (EU Cross-border) */}
        <Route 
          path="/consultations" 
          element={<Navigate to="/consultations/search" replace />}
        />
        <Route 
          path="/consultations/search" 
          element={
            <Layout>
              <ConsultationSearch />
            </Layout>
          } 
        />
        <Route 
          path="/consultations/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <ProfessionalConsultationSettings />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/consultations/my" 
          element={
            <ProtectedRoute>
              <Layout>
                <MyConsultations />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/consultations/admin" 
          element={
            <ProtectedRoute requiredRole="super_admin">
              <Layout>
                <AdminConsultationStats />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Payment */}
        <Route 
          path="/payment" 
          element={
            <ProtectedRoute>
              <Layout showFooter={false}>
                <PaymentPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Tutorial Pages */}
        <Route 
          path="/consultations/tutorial" 
          element={
            <Layout>
              <PatientTutorial />
            </Layout>
          } 
        />
        <Route 
          path="/consultations/tutorial/patient" 
          element={
            <Layout>
              <PatientTutorial />
            </Layout>
          } 
        />
        <Route 
          path="/consultations/tutorial/professional" 
          element={
            <Layout>
              <ProfessionalTutorial />
            </Layout>
          } 
        />
        
        {/* Catch all - 404 Page */}
        <Route path="*" element={<Layout><NotFound /></Layout>} />
      </Routes>
    </VisitorTracker>
    </BetaGate>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
              <PWAInstallPrompt />
              <NotificationBanner />
              <PWAStatusBar />
              <CookieBanner />
              <SessionTimeout timeoutMinutes={30} warningMinutes={5} />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
